import { useEffect, useState } from "react";

export type HeroViewportMode = "auto" | "desktop" | "mobile";

export const HERO_BACKGROUND_IMAGE_URL = "/hero.webp";
export const HERO_LANDSCAPE_VIDEO_URL = "/videos/hero/sepps-hero-video-landscape.webm";
export const HERO_PORTRAIT_VIDEO_URL = "/videos/hero/sepps-hero-video-portrait.webm";

const MOBILE_VIDEO_MEDIA_QUERY = "(max-width: 767px)";

const getIsMobileViewport = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia(MOBILE_VIDEO_MEDIA_QUERY).matches;
};

export function getFixedHeroMediaFields() {
  return {
    background_type: "video" as const,
    background_image_url: HERO_BACKGROUND_IMAGE_URL,
    background_video_url: HERO_LANDSCAPE_VIDEO_URL,
  };
}

export function useResponsiveVideoUrl(landscapeUrl: string, portraitUrl: string, viewportMode: HeroViewportMode = "auto") {
  const [isMobileViewport, setIsMobileViewport] = useState(getIsMobileViewport);

  useEffect(() => {
    if (viewportMode !== "auto" || typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_VIDEO_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [viewportMode]);

  if (viewportMode === "mobile") {
    return portraitUrl;
  }

  if (viewportMode === "desktop") {
    return landscapeUrl;
  }

  return isMobileViewport ? portraitUrl : landscapeUrl;
}

export function useHeroVideoUrl(viewportMode: HeroViewportMode = "auto") {
  return useResponsiveVideoUrl(HERO_LANDSCAPE_VIDEO_URL, HERO_PORTRAIT_VIDEO_URL, viewportMode);
}

export function getHeroVideoMimeType(url: string) {
  const lower = url.toLowerCase().split("?")[0];

  if (lower.endsWith(".mp4") || lower.endsWith(".m4v")) return "video/mp4";
  if (lower.endsWith(".webm")) return "video/webm";
  if (lower.endsWith(".ogg") || lower.endsWith(".ogv")) return "video/ogg";
  if (lower.endsWith(".mov")) return "video/quicktime";

  return "video/mp4";
}